import PropTypes from 'prop-types';
import React, { useContext, useState, useMemo, useCallback } from 'react';
import { useTranslation } from "react-i18next";
import { useDispatch } from 'react-redux';

import { LoadingContext, M_StateActions, MTable } from 'src/M_Components';

export default function CountryConfig({ roles, onCreateTab, toastMessages }) {
    const ctx = useContext(LoadingContext);
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const [refresh, setRefresh] = useState(false);

    // memoize to avoid creating a new array on every render
    const removeContextItems = useMemo(() => ['Print', 'Copy'], []);

    // Route for the CRUD tab
    const CRUD_ROUTE = '/home/countries/countryConfigCrud';

    //=================================================================================
    // openCountryCrud: centralizes tab opening logic for Countries.
    // - Persists the current operation in the shared state.
    // - Validates that a row is present for View/Edit/Copy operations.
    // - Computes the tab label using i18n keys, avoiding passing labels around.
    // - Uses useCallback to keep a stable function reference across renders,
    //      preventing unnecessary re-renders of children (like the MTable).
    //=================================================================================
    const openCountryCrud = useCallback(
        (operation, data) => {
            // Persist operation
            dispatch(M_StateActions.addM_State({ key: 'operation', value: operation }));

            // For operations that require a selected row, validate and persist it
            if (['Edit', 'Copy', 'View'].includes(operation)) {
                if (!data) {
                    toastMessages('error', '', t('SelectedLine'));
                    return;
                }
                dispatch(M_StateActions.addM_State({ key: 'CountryLine', value: data }));
            }

            // Build the tab label using translation keys to keep logic in one place
            const tabLabel =
                operation === 'Create'
                    ? t('LabelNewCountry')
                    : operation === 'View'
                    ? `${t('LabelViewCountry')} "${data?.Name ?? ''}"`
                    : `${t('LabelEditCountry')} "${data?.Name ?? ''}"`;

            onCreateTab(CRUD_ROUTE, tabLabel);
        },
        [dispatch, onCreateTab, t, toastMessages]
    );

   
    //=================================================================================
    // replaceFunctions map:
    // - Memoized with useMemo to provide a stable identity to MTable.
    // - Prevents MTable from re-rendering due to a new Map instance each render.
    // - Delegates to openCountryCrud for actual navigation/side-effects.
    // ===============================================================================
    const replaceFunctions = useMemo(
        () =>
            new Map([
                ['View', (row) => openCountryCrud('View', row)],
                ['Create', () => openCountryCrud('Create')],
                ['Edit', (row) => openCountryCrud('Edit', row)],
            ]),
        [openCountryCrud]
    );

    return (
        <div className='divGlobalSettings'>
            <div className='subTitleSettings'>
                <div>
                    <div style={{ color: ctx.secondaryColor }}>{t("Countries")}</div>
                    <div className='labelSubtitleSettings'>{t("InfoCountries")}</div>
                </div>
            </div>
            <MTable // Dynamic table for Country
                filtersOnTable
                entityName={'Countries'}
                roleName={'Locations'}
                rolePermissions={roles?.map(vl => vl.Value)} // Array of permission strings for Locations Module
                removeContextItems={removeContextItems}
                refresh={refresh}
                setRefresh={setRefresh}
                useSessionStorage={false}
                replaceFunctions={replaceFunctions} // Map of functions to replace default actions
            />
        </div>
    );
}

CountryConfig.propTypes = {
    roles: PropTypes.array,
    onCreateTab: PropTypes.func,
    toastMessages: PropTypes.func
};
